import { showUserAlert } from "../../utils/userErrorMessage";
// src/pages/accounts/CommuteUnpaidDetail.jsx
import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabase/client";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { applyClosingDateToRows, ClosingDaySelect, CLOSING_DAY_LAST, markRowsPaidByClosingDate } from "./closingDateUtils";
import { clampDateString, normalizeDateRange, validateNativeDateInputs } from "../../utils/dateUtils";

const getToday = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

export default function CommuteUnpaidDetail({ closingDay = CLOSING_DAY_LAST, setClosingDay = () => {} }) {
  const [dateRange, setDateRange] = useState({ from: getToday(), to: getToday() });
  const [filters, setFilters] = useState({
    사용자: "",
    출발지: "",
    행선지: "",
    차량번호: "",
    기사명: "",
    금액: "",
  });
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [showSubtotalOnly, setShowSubtotalOnly] = useState(false); // ✅ 추가
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);

  // ✅ 전체 합계 계산 유틸
  const calcGrandTotal = (list) => {
    let money = 0;
    let unpaid = 0;
    for (const row of list) {
      money += row.money || 0;
      unpaid += (row.money || 0) - (row.payment || 0);
    }
    return { money, unpaid };
  };
  
  const grand = calcGrandTotal(data);

  const handleMarkPaidAll = async (userKey) => {
    // 이 사용자 그룹의 행들
    const userRows = data.filter((row) => (row.user || "(미지정)") === userKey);

    // 아직 입금처리(misu_date)가 안 된 것만
    const idsToUpdate = userRows
      .filter((r) => !r.misu_date)
      .map((r) => r.id);

    if (idsToUpdate.length === 0) {
      showUserAlert("이미 모두 입금 처리되어 있습니다.");
      return;
    }

    const ok = window.confirm(
      `${userKey} 항목 ${idsToUpdate.length}건을 모두 입금 완료 처리할까요?`
    );
    if (!ok) return;

    try {
      await markRowsPaidByClosingDate(supabase, userRows, closingDay);
    } catch (error) {
      showUserAlert("일괄 입금 처리 실패: " + error.message);
      return;
    }

    // 새로고침
    fetchData(filters);
  };



  const fetchData = async (filtersParam = filters, currentDateRange = dateRange) => {
    const companyId = localStorage.getItem("email");

    // ✅ 1000개 제한을 우회하기 위한 전체 페이지 로더
    const fetchAll = async (baseQuery) => {
      const pageSize = 1000;
      let from = 0;
      let all = [];

      while (true) {
        const { data, error } = await baseQuery.range(from, from + pageSize - 1);
        if (error) throw error;

        all = all.concat(data || []);
        if (!data || data.length < pageSize) break; // 마지막 페이지
        from += pageSize;
      }

      return all;
    };

    try {
      let query = supabase
        .from("reservations")
        .select("*") // ✅ fetchAll에서 페이지를 나눠 받으니 count는 선택
        .eq("company", companyId)
        .eq("reservation_type", "통근");

      if (currentDateRange.from) query = query.gte("date", currentDateRange.from);
      if (currentDateRange.to) query = query.lte("date", currentDateRange.to);

      // ✅ 정렬 고정 (업데이트 후 결과가 바뀌는 현상 방지)
      query = query.order("date", { ascending: true }).order("id", { ascending: true });

      // ✅ 전체 데이터 가져오기
      const result = await fetchAll(query);
      const targetMonth = currentDateRange.from ? currentDateRange.from.slice(0, 7) : "";
      const rowsWithClosing = await applyClosingDateToRows(supabase, result || [], closingDay, targetMonth);

      // 기존 프론트 필터 적용
      const filtered = rowsWithClosing.filter(
        (row) =>
          (!filtersParam["사용자"] || (row.user ?? "").includes(filtersParam["사용자"])) &&
          (!filtersParam["출발지"] || (row.start ?? "").includes(filtersParam["출발지"])) &&
          (!filtersParam["행선지"] || (row.destination ?? "").includes(filtersParam["행선지"])) &&
          (!filtersParam["차량번호"] || (row.car_number ?? "").includes(filtersParam["차량번호"])) &&
          (!filtersParam["기사명"] || (row.human_name ?? "").includes(filtersParam["기사명"])) &&
          (!filtersParam["금액"] || ((row.money ?? 0).toString().includes(filtersParam["금액"])))
      );

      setData(filtered);
      calculateSummary(filtered);
    } catch (e) {
      console.error("Supabase 데이터 오류:", e?.message || e);
      setData([]);
      setSummary({});
    }
  };


  const calculateSummary = (list) => {
    const grouped = {};
    list.forEach((item) => {
      const key = item.user || "(미지정)";
      if (!grouped[key]) {
        grouped[key] = { money: 0, unpaid: 0 };
      }
      grouped[key].money += item.money || 0;
      grouped[key].unpaid += (item.money || 0) - (item.payment || 0);
    });
    setSummary(grouped);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closingDay]);

  const handleFilter = () => {
    if (!validateNativeDateInputs([fromDateInputRef.current, toDateInputRef.current])) return;
    const nextDateRange = normalizeDateRange(dateRange);
    setDateRange(nextDateRange);
    fetchData(filters, nextDateRange);
  };

  const handleMarkPaid = async (id) => {
    const row = data.find((item) => item.id === id);
    try {
      await markRowsPaidByClosingDate(supabase, row ? [row] : [], closingDay);
      fetchData(filters);
    } catch (error) {
      showUserAlert("입금 처리 실패: " + error.message);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {Object.entries(filters).map(([key, value]) => (
          <Input
            key={key}
            placeholder={key}
            value={value}
            onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
            className="w-40"
          />
        ))}
        <Input
          ref={fromDateInputRef}
          type="date"
          value={dateRange.from}
          onChange={(e) => setDateRange({ ...dateRange, from: clampDateString(e.target.value) })}
        />
        <Input
          ref={toDateInputRef}
          type="date"
          value={dateRange.to}
          onChange={(e) => setDateRange({ ...dateRange, to: clampDateString(e.target.value) })}
        />
        <Button onClick={handleFilter}>검색</Button>

        {/* ✅ 소계만 보여주기 */}
        <label className="ml-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showSubtotalOnly}
            onChange={(e) => setShowSubtotalOnly(e.target.checked)}
          />
          소계만 보여주기
        </label>
        <ClosingDaySelect closingDay={closingDay} onChange={setClosingDay} />
      </div>

      <Card>
        <CardContent className="bg-white">
          <div className="overflow-x-auto">
            <table className="table-fixed w-full border border-gray-300 text-sm [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1">
              <colgroup>
                <col className="w-10" />
                <col className="w-12" />
                <col className="w-[140px]" />
                <col className="w-[96px]" />
                <col className="w-[22%]" />
                <col className="w-[22%]" />
                <col className="w-[96px]" />
                <col className="w-[90px]" />
                <col className="w-[92px]" />
                <col className="w-[96px]" />
                <col className="w-[92px]" />
              </colgroup>
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th colSpan={9} className="border border-gray-300 p-2">배차 정보</th>
                  <th colSpan={2} className="border border-gray-300 p-2">금액 정보</th>
                </tr>
                <tr>
                  {[
                    "순번", "구분", "사용자", "운행일", "출발지", "행선지",
                    "차량번호", "기사명", "받을금액", "입금 일자", "금액"
                  ].map((header) => (
                    <th
                      key={header}
                      className={`border border-gray-300 p-2 ${["순번", "구분", "차량번호", "기사명"].includes(header) ? "text-center" : ""}`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* ✅ 항상 최상단에 전체 합계 */}
                <tr className="bg-amber-100 font-bold">
                  <td className="border p-2 text-center" colSpan={8}>전체 합계</td>
                  <td className="border p-2 text-right">{grand.money.toLocaleString()}</td>
                  <td className="border p-2 text-center"></td>
                  <td className="border p-2 text-right">{grand.unpaid.toLocaleString()}</td>
                </tr>
                {Object.entries(summary).map(([user, sums]) => {
                  const userData = data.filter((row) => (row.user || "(미지정)") === user);

                  // ✅ 소계만 모드: 상세 행 없이 소계만
                  if (showSubtotalOnly) {
                    return (
                      <tr key={`subtotal-${user}`} className="bg-gray-100 font-semibold">
                        <td className="border p-2 text-center" colSpan={8}>{user} 소계</td>
                        <td className="border p-2 text-right">{sums.money.toLocaleString()}</td>
                        {/* ✅ 입금 일자 열에 "모두 입금 완료" 버튼 */}
                        <td className="border p-2 text-center">
                          <Button size="sm" className="text-xs whitespace-nowrap px-2" onClick={() => handleMarkPaidAll(user)}>
                            모두 입금
                          </Button>
                        </td>
                        <td className="border p-2 text-right">{sums.unpaid.toLocaleString()}</td>
                      </tr>
                    );
                  }

                  // ✅ 기본: 상세 + 소계
                  return (
                    <React.Fragment key={user}>
                      {userData.map((row, index) => (
                        <tr key={row.id}>
                          <td className="border border-gray-300 p-2 text-center">{index + 1}</td>
                          <td className="border border-gray-300 p-2 text-center">{row.reservation_type}</td>
                          <td className="border border-gray-300 p-2">{row.user}</td>
                          <td className="border border-gray-300 p-2">{row.date}</td>
                          <td className="border border-gray-300 p-2">{row.start}</td>
                          <td className="border border-gray-300 p-2">{row.destination}</td>
                          <td className="border border-gray-300 p-2 text-center">{row.car_number}</td>
                          <td className="border border-gray-300 p-2 text-center">{row.human_name}</td>
                          <td className="border border-gray-300 p-2 text-right">
                            {row.money?.toLocaleString()}
                          </td>
                          <td className="border border-gray-300 p-2 text-center">
                            {row.misu_date ? (
                              row.misu_date
                            ) : (
                              <Button size="sm" onClick={() => handleMarkPaid(row.id)}>
                                입금
                              </Button>
                            )}
                          </td>
                          <td className="border border-gray-300 p-2 text-right">
                            {((row.money || 0) - (row.payment || 0)).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 font-semibold">
                        <td className="border p-2 text-center" colSpan={8}>{user} 소계</td>
                        <td className="border p-2 text-right">{sums.money.toLocaleString()}</td>                        
                        <td className="border p-2 text-center">
                          <Button size="sm" className="text-xs whitespace-nowrap px-2" onClick={() => handleMarkPaidAll(user)}>
                            모두 입금
                          </Button>
                        </td>
                        <td className="border p-2 text-right">{sums.unpaid.toLocaleString()}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
